import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation, Link } from "wouter";
import { Helmet } from "react-helmet-async";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Share2, ChevronRight, History, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import type { Page, LegalDocVersion } from "@shared/schema";

export default function PublicPage() {
  const params = useParams<{ slug: string }>();
  const [, setLocation] = useLocation();
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  const { data: page, isLoading, error } = useQuery<Page>({
    queryKey: ["/api/pages/slug", params.slug],
    queryFn: async () => {
      const response = await fetch(`/api/pages/slug/${params.slug}`);
      if (!response.ok) {
        throw new Error("Page not found");
      }
      return response.json();
    },
    enabled: !!params.slug,
  });

  const isLegal = page?.pageType === "legal";

  const { data: allVersions = [] } = useQuery<LegalDocVersion[]>({
    queryKey: ["/api/legal/versions", params.slug],
    queryFn: async () => {
      const res = await fetch(`/api/legal/${params.slug}/versions`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: isLegal,
    staleTime: 0,
  });

  // allVersions[0] = latest = current live version (legalDocVersions stores new content on each save)
  const currentVersion = allVersions.length > 0 ? allVersions[0] : null;
  // Previous versions = all entries except the latest (which is the current live content)
  const versions = allVersions.slice(1);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />
        <main className="flex-1">
          <div className="container mx-auto px-4 py-12 max-w-4xl">
            <Skeleton className="h-10 w-64 mb-4" />
            <Skeleton className="h-6 w-48 mb-8" />
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Page Not Found</h1>
            <p className="text-muted-foreground mb-6">
              The page you're looking for doesn't exist or has been removed.
            </p>
            <Button onClick={() => setLocation("/")} data-testid="button-go-home">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go Home
            </Button>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{page.title} | Future Manufacturing</title>
        {page.metaDescription && (
          <meta name="description" content={page.metaDescription} />
        )}
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />

        <main className="flex-1">
          {page.featuredImage && (
            <div className="relative w-full h-64 md:h-80 bg-muted overflow-hidden">
              <img
                src={page.featuredImage}
                alt={page.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
            </div>
          )}

          <div className="container mx-auto px-4 py-10 max-w-4xl">
            {/* Breadcrumb */}
            {isLegal ? (
              <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
                <Link href="/" className="hover:text-foreground transition-colors">
                  Home
                </Link>
                <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                <Link href="/legal" className="hover:text-foreground transition-colors">
                  Legal
                </Link>
                <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                <span className="text-foreground font-medium">{page.title}</span>
              </nav>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLocation("/")}
                className="mb-6"
                data-testid="button-back-home"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            )}

            <header className="mb-8">
              <h1
                className="text-3xl md:text-4xl font-bold mb-4"
                data-testid="text-page-title"
              >
                {page.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                {page.updatedAt && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Updated {format(new Date(page.updatedAt), "MMMM d, yyyy")}
                  </span>
                )}
                {isLegal && currentVersion && (
                  <span className="font-mono font-semibold text-foreground" data-testid="text-current-version">
                    Version {currentVersion.versionNumber}
                    {currentVersion.effectiveDate && (
                      <span className="font-normal text-muted-foreground ml-1.5">
                        · Effective {format(new Date(currentVersion.effectiveDate), "d MMM yyyy")}
                      </span>
                    )}
                  </span>
                )}
              </div>
            </header>

            {page.content && (
              <article
                className="prose prose-lg dark:prose-invert max-w-none [&_p]:mb-4 [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg [&_a]:text-primary [&_a]:underline [&_table]:w-full [&_th]:text-left [&_th]:p-2 [&_td]:p-2 [&_table]:border-collapse [&_th]:border [&_td]:border"
                dangerouslySetInnerHTML={{ __html: page.content }}
                data-testid="content-page-body"
              />
            )}

            {/* Version history — legal pages only */}
            {isLegal && (
              <div className="mt-10 border rounded-lg overflow-hidden" data-testid="section-version-history">
                <button
                  className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-muted-foreground hover:bg-muted/50 transition-colors"
                  onClick={() => setShowVersionHistory(v => !v)}
                  data-testid="button-toggle-version-history"
                  aria-expanded={showVersionHistory}
                >
                  <span className="flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Previous versions
                  </span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${showVersionHistory ? "rotate-180" : ""}`} />
                </button>

                {showVersionHistory && (
                  <div className="border-t divide-y text-sm" data-testid="list-version-history">
                    {versions.length === 0 ? (
                      <p className="px-4 py-3 text-muted-foreground">No previous versions on record.</p>
                    ) : (
                      versions.map(v => (
                        <div key={v.id} className="flex items-center justify-between px-4 py-3">
                          <div className="space-y-0.5">
                            <span className="font-mono font-medium">Version {v.versionNumber}</span>
                            {v.effectiveDate && (
                              <span className="text-muted-foreground ml-3 text-xs">
                                Effective {format(new Date(v.effectiveDate), "d MMM yyyy")}
                              </span>
                            )}
                            {v.changeDescription && (
                              <p className="text-muted-foreground text-xs mt-0.5">{v.changeDescription}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(v.createdAt), "d MMM yyyy")}
                            </span>
                            <Link
                              href={`/legal/${page.slug}/v/${v.versionNumber}`}
                              className="text-primary text-xs hover:underline"
                              data-testid={`link-version-${v.versionNumber}`}
                            >
                              View
                            </Link>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            <footer className="mt-12 pt-6 border-t">
              <div className="flex items-center justify-between">
                {isLegal ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setLocation("/legal")}
                    data-testid="button-back-legal"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    All legal documents
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setLocation("/")}
                    data-testid="button-back-home-footer"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Home
                  </Button>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                  }}
                  data-testid="button-share-page"
                >
                  <Share2 className="mr-2 h-4 w-4" />
                  Share
                </Button>
              </div>
            </footer>
          </div>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
